import { useState } from 'react';
import { LoggerIcon } from './LoggerIcon';
import { Route, CalendarClock, Play, Volume2, ScanSearch } from 'lucide-react';
import { flyTo, setVis } from '../lib/vis';
import { startRound, openPlanTab } from './lpShared';
import { orderStops, routeLegs } from '../lib/route';
import { loggerState, activeLoggers, noiseSpots } from '../hooks/useLoggers';

// 1-click Google Maps routes: due lift & shift loggers / all loggers out
export const loggerSets = (project) => {
  const all = activeLoggers(project);
  return { due: all.filter((l) => ['red', 'yellow'].includes(loggerState(l))), all };
};

export default function LoggerRoutes({ t, project, start, compact, updateProject }) {
  const { due, all } = loggerSets(project);
  const [legs, setLegs] = useState([]);
  const [order, setOrder] = useState([]);
  const lifts = all.filter((l) => l.mode !== 'perm' && l.dueAt).sort((a, b) => a.dueAt - b.dueAt);
  const plan = project.loggerPlan || [], planOpen = plan.filter((p) => !p.doneAt);
  const showPlan = compact && planOpen.length > 0 && updateProject;
  const noise = compact ? noiseSpots(project) : [];
  if (!all.length && !showPlan && !noise.length) return null;
  // route always starts at the current position (GPS); Google Maps "my location" if no fix
  const go = async (list, keep) => {
    const w = window.open('', '_blank');
    const here = start || await new Promise((res) => {
      if (!navigator.geolocation) return res(null);
      navigator.geolocation.getCurrentPosition((p) => res({ lat: p.coords.latitude, lon: p.coords.longitude }), () => res(null), { enableHighAccuracy: true, timeout: 6000, maximumAge: 60000 });
    });
    const stops = list.map((l) => ({ ...l, title: l.name })), ord = keep ? stops : orderStops(here || stops[0], stops);
    const ls = routeLegs(here, ord);
    setOrder(ord);
    if (w) w.location.href = ls[0].url; else window.open(ls[0].url, '_blank', 'noopener');
    setLegs(ls.length > 1 ? ls : []);
  };
  return (
    <div className={`lg-routes ${compact ? 'lg-routes-card panel' : ''}`} data-testid={compact ? 'logger-card' : 'logger-routes'}>
      {compact && <b className="lg-card-title"><LoggerIcon size={16} /> {t.lgTitle}: <span data-testid="logger-count-out">{all.length}</span> {t.lgOut} · <span data-testid="logger-count-due">{due.length}</span> {t.lgDueShort}</b>}
      {showPlan && (
        <div className="lg-card-plan" data-testid="card-plan-round">
          <b data-testid="card-plan-count">🗺 {t.lrTitle}: {planOpen.length} {t.lrPlanned}</b>
          <button className="btn btn-green btn-sm" data-testid="card-plan-start-btn" onClick={() => { startRound(plan, (list) => updateProject((p) => ({ ...p, loggerPlan: list })), plan.some((p) => p.doneAt)); openPlanTab('deploy'); }}><Play size={14} /> {plan.some((p) => p.doneAt) ? t.lrGoOn : t.lrStart}</button>
        </div>
      )}
      {noise.length > 0 && (
        <div className="lg-card-plan" data-testid="card-noise">
          <b className="ns-count" data-testid="card-noise-count"><Volume2 size={14} /> {noise.length} {noise.length === 1 ? t.nsOne : t.nsTitle}</b>
          <button className="btn btn-outline btn-sm" data-testid="card-noise-show-btn" onClick={() => { setVis({ noise: true }); flyTo(noise.map((s) => [s.lat, s.lon])); }}><ScanSearch size={14} /> {t.nsShow}</button>
          <button className="btn btn-red btn-sm" data-testid="card-noise-route-btn" onClick={() => { setVis({ noise: true }); go(noise.map((s) => ({ ...s, name: s.id.endsWith('-c') ? s.name : `${s.name} (${t.nsOld})` }))); }}><Route size={14} /> {t.nsRoute}</button>
        </div>
      )}
      {all.length > 0 && <>
      <button className="btn btn-red btn-sm" data-testid={`${compact ? 'card' : 'route'}-logger-due-btn`} disabled={!due.length} onClick={() => go(due)}><Route size={14} /> {t.lgRouteDue} ({due.length})</button>
      {lifts.length > 0 && <button className="btn btn-outline btn-sm" data-testid={`${compact ? 'card' : 'route'}-logger-bydue-btn`} onClick={() => go(lifts, true)}><CalendarClock size={14} /> {t.lpRouteDue} ({lifts.length})</button>}
      <button className="btn btn-primary btn-sm" data-testid={`${compact ? 'card' : 'route'}-logger-all-btn`} onClick={() => go(all)}><LoggerIcon size={14} /> {t.lgRouteAll} ({all.length})</button>
      </>}
      {legs.map((l, i) => <a key={l.url} className="btn btn-outline btn-sm" data-testid={`logger-leg-${i}`} href={l.url} target="_blank" rel="noreferrer"><Route size={13} /> {t.routeBtn} {i + 1}/{legs.length}</a>)}
      {order.length > 0 && <ol className="lg-order" data-testid="logger-route-order"><li>📍 {t.lgFromHere}</li>{order.map((l) => <li key={l.id}>{l.name}{l.dueAt ? ` · ${new Date(l.dueAt).toLocaleString(t.locale, { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}` : ''}</li>)}</ol>}
    </div>
  );
}
